import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import './attendance.css';
import { Card, CardContent, CardHeader, CardTitle } from "../../component/ui/Card";
import axios from 'axios';

const Attendance = ({ joinDate, userId }) => {
  const totalLeave = 19; // Total paid leaves in the financial year (April 1st to March 31st)
  const [usedLeave, setUsedLeave] = useState(0);
  const [employeeLeave, setEmployeeLeave] = useState(0);
  const [isEligible, setIsEligible] = useState(false);
  const [leaveDates, setLeaveDates] = useState([]);

  useEffect(() => {
    const fetchLeaveData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}attendance/${userId}`);
        const data = response.data;

        setLeaveDates(data.leaveDates);
        setUsedLeave(data.usedLeave);
        setEmployeeLeave(data.availableLeave);
      } catch (error) {
        console.error("Error fetching attendance data:", error);
      }
    };

    fetchLeaveData();
  }, [userId]);

  const toISTDateString = (date) => {
    const options = { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit" };
    return new Intl.DateTimeFormat("en-CA", options).format(date); 
  };

  const getLeaveEligibility = () => {
    if (!joinDate) return 0;

    const joinMonth = new Date(joinDate).getMonth();
    const sixMonthsComplete = new Date(new Date(joinDate).setMonth(joinMonth + 6));

    const currentDate = new Date();
    if (sixMonthsComplete <= currentDate) {
      setIsEligible(true);
      return totalLeave;
    }

    return 0;
  };

  const employeeLeaveBalance = getLeaveEligibility();

  const [leaveType, setLeaveType] = useState("paid");
  const [reason, setReason] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const handleLeaveTypeChange = (event) => {
    setLeaveType(event.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const leaveData = {
      leaveType,
      startDate,
      endDate,
      reason,
    };

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}apply-leave/${userId}`, leaveData);
      console.log(response.data);
    } catch (error) {
      console.error("Error applying for leave:", error);
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto bg-white rounded-lg shadow-lg space-y-8">
      {!isEligible && (
        <div className="bg-yellow-100 p-4 rounded-md shadow-md">
          <p className="text-lg text-gray-700">Employee leave eligibility is calculated after 6 months of joining.</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        <Card className="shadow-xl hover:shadow-2xl transition-shadow duration-300">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-800">Total Leave Available</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-teal-600">{employeeLeaveBalance}</p>
          </CardContent>
        </Card>

        <Card className="shadow-xl hover:shadow-2xl transition-shadow duration-300">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-800">Total Leave Used</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-red-600">{usedLeave}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        <div className="col-span-1 sm:col-span-1">
          <Card className="shadow-xl hover:shadow-2xl transition-shadow duration-300">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-800">Leave Calendar</CardTitle>
            </CardHeader>
            <CardContent>
              <Calendar
                tileClassName={({ date }) => {
                  const formattedDate = toISTDateString(date);
                  return leaveDates.includes(formattedDate) ? "highlight-leave" : "";
                }}
              />
            </CardContent>
          </Card>
        </div>

        <div className="col-span-1 sm:col-span-1">
          <Card className="shadow-xl hover:shadow-2xl transition-shadow duration-300">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-800">Apply for Leave</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-700 font-medium">Leave Type</label>
                  <select
                    value={leaveType}
                    onChange={handleLeaveTypeChange}
                    className="w-full p-3 border rounded-md shadow-sm focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="paid">Paid Leave</option>
                    <option value="unpaid">Unpaid Leave</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full p-3 border rounded-md shadow-sm focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full p-3 border rounded-md shadow-sm focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium">Reason</label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full p-3 border rounded-md shadow-sm focus:ring-2 focus:ring-teal-500"
                    rows="4"
                    required
                  />
                </div>

                <div className="flex justify-end">
                  <button type="submit" className="bg-teal-600 text-white p-3 rounded-md hover:bg-teal-700 focus:outline-none">
                    Submit Leave Request
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Attendance;
